import React, { useState, useEffect } from "react";
import {
    Box, TextField, Button, MenuItem, Select, FormControl, InputLabel,
    Paper, Typography, Stack, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, IconButton, Dialog, DialogTitle, DialogContent,
    DialogActions, Chip, InputAdornment, TablePagination, CircularProgress
} from "@mui/material";
import { Add, Search, Edit, Delete } from "@mui/icons-material";
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, query, orderBy } from "firebase/firestore";
import { db } from "../../firebase";

function Advancepayment() {
    const [staffList, setStaffList] = useState([]);
    const [advances, setAdvances] = useState([]);
    const [search, setSearch] = useState("");
    const [open, setOpen] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [currentId, setCurrentId] = useState(null);
    const [globalLoading, setGlobalLoading] = useState(true);

    // Pagination
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const [form, setForm] = useState({
        staffId: "",
        payment: "",
        date: new Date().toLocaleDateString('en-CA'),
        payType: "Cash",
        note: "",
        status: "Pending"
    });

    useEffect(() => {
        const initializeData = async () => {
            setGlobalLoading(true);
            await Promise.all([loadStaff(), loadAdvances()]);
            setGlobalLoading(false);
        };
        initializeData();
    }, []);

    const loadStaff = async () => {
        try {
            const snapshot = await getDocs(collection(db, "authroles"));
            setStaffList(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        } catch (error) {
            console.error("Error structuralizing user base configuration:", error);
        }
    };

    const loadAdvances = async () => {
        try {
            const q = query(collection(db, "staffadvances"), orderBy("date", "desc"));
            const snapshot = await getDocs(q);
            setAdvances(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        } catch (error) {
            console.error("Error tracing systemic advance ledgers:", error);
        }
    };

    const handleOpen = (data = null) => {
        if (data) {
            setEditMode(true);
            setCurrentId(data.id);
            setForm({
                staffId: data.staffId || "",
                payment: data.payment || "",
                date: data.date || new Date().toLocaleDateString('en-CA'),
                payType: data.payType || "Cash",
                note: data.note || "",
                status: data.status || "Pending"
            });
        } else {
            setEditMode(false);
            setCurrentId(null);
            setForm({ 
                staffId: "", 
                payment: "", 
                date: new Date().toLocaleDateString('en-CA'), 
                payType: "Cash", 
                note: "",
                status: "Pending"
            });
        }
        setOpen(true);
    };

    const handleSave = async () => {
        if (!form.staffId || !form.payment) {
            return alert("Please map a selected staff member and input an amount.");
        }

        const data = { 
            ...form, 
            payment: Number(form.payment), 
            status: form.status || "Pending" 
        };

        try {
            if (editMode && currentId) {
                await updateDoc(doc(db, "staffadvances", currentId), data);
            } else {
                await addDoc(collection(db, "staffadvances"), data);
            }
            setOpen(false);
            loadAdvances();
        } catch (error) {
            console.error("Could not write record safely:", error);
            alert("Error parsing document adjustments onto database matrix. Check access flags.");
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm("Are you sure you want to permanently delete this allocation log?")) {
            try {
                await deleteDoc(doc(db, "staffadvances", id));
                loadAdvances();
            } catch (error) {
                console.error("Deletion target baseline failure:", error);
            }
        }
    };

    const getStaffName = (id) => {
        const staff = staffList.find(s => s.id === id);
        return staff ? (staff.name || staff.displayName || "Named Staff Member") : "Unknown Staff";
    };

    // Filter Logic Integration
    const filteredData = advances.filter(item => {
        const matchingName = getStaffName(item.staffId).toLowerCase();
        const matchingType = (item.payType || "").toLowerCase();
        const searchTerm = search.toLowerCase();
        return matchingName.includes(searchTerm) || matchingType.includes(searchTerm);
    });

    if (globalLoading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="70vh">
                <CircularProgress size={40} thickness={4.5} />
            </Box>
        );
    }

    return (
        <Box sx={{ bgcolor: '#f4f6f8', minHeight: '100vh', p: 1 }}>
            <Paper sx={{ p: 3, borderRadius: 4, boxShadow: '0 4px 20px rgba(0,0,0,0.04)' }}>
                
                {/* Header Row */}
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="space-between" alignItems="center" mb={4}>
                    <Box>
                        <Typography variant="h5" fontWeight="800" color="primary.main">Advance Ledger Management</Typography>
                        <Typography variant="caption" color="textSecondary">Manage and track advanced allocation items outside normal processing channels</Typography>
                    </Box>
                    <Button 
                        variant="contained" 
                        startIcon={<Add />} 
                        onClick={() => handleOpen()}
                        sx={{ borderRadius: 2, px: 3, py: 1, textTransform: 'none', fontWeight: 'bold' }}
                    >
                        Issue New Advance
                    </Button>
                </Stack>

                {/* Filters Row */}
                <Box mb={3} display="flex" justifyContent="flex-start">
                    <TextField
                        placeholder="Search by name or route profile..."
                        variant="outlined"
                        size="small"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        sx={{ width: { xs: '100%', sm: '320px' } }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Search color="action" />
                                </InputAdornment>
                            ),
                            sx: { borderRadius: 3, bgcolor: '#fff' }
                        }}
                    />
                </Box>

                {/* Ledger Record Table */}
                <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #eee', borderRadius: 3, mb: 2 }}>
                    <Table size="small">
                        <TableHead sx={{ bgcolor: '#f8f9fa' }}>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 'bold', py: 1.5 }}>Date</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', py: 1.5 }}>Staff Identity</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', py: 1.5 }}>Advanced Balance</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', py: 1.5 }}>Type</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', py: 1.5 }}>Status</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', py: 1.5 }} align="right">Operational Adjustments</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row) => (
                                <TableRow key={row.id} hover>
                                    <TableCell>{row.date}</TableCell>
                                    <TableCell>
                                        <Typography variant="body2" fontWeight="bold">{getStaffName(row.staffId)}</Typography>
                                        {row.note && <Typography variant="caption" color="textSecondary" display="block">{row.note}</Typography>}
                                    </TableCell>
                                    <TableCell>
                                        <Typography fontWeight="bold" color="error.main">₹{(row.payment || 0).toLocaleString('en-IN')}</Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Chip label={row.payType || "Cash"} size="small" variant="outlined" sx={{ borderRadius: 1.5 }} />
                                    </TableCell>
                                    <TableCell>
                                        <Chip 
                                            label={row.status || "Pending"} 
                                            size="small" 
                                            color={row.status === "Settled" || row.status === "Approved" ? "success" : "warning"}
                                            sx={{ fontWeight: 'bold', borderRadius: 1.5 }}
                                        />
                                    </TableCell>
                                    <TableCell align="right">
                                        <IconButton size="small" color="primary" onClick={() => handleOpen(row)}><Edit fontSize="small" /></IconButton>
                                        <IconButton size="small" color="error" onClick={() => handleDelete(row.id)}><Delete fontSize="small" /></IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                            {filteredData.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={6} align="center" sx={{ py: 6, color: 'text.secondary' }}>
                                        No metrics found matching current database configuration filters.
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                <TablePagination
                    rowsPerPageOptions={[5, 10, 20, 50]}
                    component="div"
                    count={filteredData.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={(e, newPage) => setPage(newPage)}
                    onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                />
            </Paper>

            {/* Action Popup Dialog Sheet */}
            <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="xs" PaperProps={{ sx: { borderRadius: 4 } }}>
                <DialogTitle sx={{ fontWeight: 'bold', bgcolor: '#f8f9fa', borderBottom: '1px solid #eee' }}>
                    {editMode ? "Modify Advanced Ledger Log" : "Construct Advance Entry"}
                </DialogTitle>
                <DialogContent sx={{ mt: 2.5 }}>
                    <Stack spacing={2.5}>
                        <FormControl fullWidth size="small">
                            <InputLabel id="select-staff-label">Select Target Personnel</InputLabel>
                            <Select 
                                labelId="select-staff-label"
                                value={form.staffId}
                                label="Select Target Personnel"
                                onChange={(e) => setForm({...form, staffId: e.target.value})}
                            >
                                {staffList.map(s => (
                                    <MenuItem key={s.id} value={s.id}>
                                        {s.name || s.displayName || s.email || "Unstructured Profiler"}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <TextField 
                            label="Issued Amount (₹)" 
                            type="number" 
                            fullWidth 
                            size="small" 
                            value={form.payment}
                            onChange={(e) => setForm({...form, payment: e.target.value})}
                        />

                        <TextField 
                            type="date" 
                            label="Disbursed Timestamp" 
                            fullWidth 
                            size="small" 
                            InputLabelProps={{ shrink: true }}
                            value={form.date}
                            onChange={(e) => setForm({...form, date: e.target.value})}
                        />

                        <FormControl fullWidth size="small">
                            <InputLabel id="select-paytype-label">Payment Processing Pipeline</InputLabel>
                            <Select
                                labelId="select-paytype-label"
                                value={form.payType}
                                label="Payment Processing Pipeline"
                                onChange={(e) => setForm({...form, payType: e.target.value})}
                            >
                                <MenuItem value="Cash">Cash Handout</MenuItem>
                                <MenuItem value="Online">Online Routing / UPI</MenuItem>
                                <MenuItem value="Bank Transfer">Direct Wire Transfer</MenuItem>
                            </Select>
                        </FormControl>

                        {editMode && (
                            <FormControl fullWidth size="small">
                                <InputLabel id="select-status-label">Operational Status</InputLabel>
                                <Select
                                    labelId="select-status-label"
                                    value={form.status}
                                    label="Operational Status"
                                    onChange={(e) => setForm({...form, status: e.target.value})}
                                >
                                    <MenuItem value="Pending">Pending Audit</MenuItem>
                                    <MenuItem value="Approved">Approved / Active</MenuItem>
                                    <MenuItem value="Settled">Settled / Deducted</MenuItem>
                                </Select>
                            </FormControl>
                        )}

                        <TextField 
                            label="Transaction Purpose / Internal Notes" 
                            placeholder="Add reason for payment tracing metrics..."
                            multiline 
                            rows={2} 
                            fullWidth 
                            size="small" 
                            value={form.note}
                            onChange={(e) => setForm({...form, note: e.target.value})}
                        />
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ p: 2.5, bgcolor: '#f8f9fa', borderTop: '1px solid #eee' }}>
                    <Button onClick={() => setOpen(false)} color="inherit" sx={{ textTransform: 'none' }}>Discard Changes</Button>
                    <Button variant="contained" onClick={handleSave} sx={{ borderRadius: 2, px: 3, textTransform: 'none' }}>
                        {editMode ? "Confirm Adjustments" : "Commit Record"}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export default Advancepayment;